<script setup lang="ts">
import { computed, ref } from "vue";
import Icon from "./Icon.vue";
import Modal from "./Modal.vue";
import WaText from "./WaText.vue";
import { buttonIcon, shownButtons } from "../composables/waButtons";
import MessageStatus from "./MessageStatus.vue";
import { messageDelivery } from "../composables/messageDelivery";
import AudioPlayer from "./AudioPlayer.vue";
import type { Message } from "../data/demo";
import { vote } from "../composables/chat";
import { groupedReactions } from "../composables/reactions";
import { extensionLabel, fileTone } from "../composables/mediaPrep";
import { t } from "../i18n";
const props = withDefaults(
  defineProps<{
    message: Message;
    selected?: boolean;
    selecting?: boolean;
    /** False for the messages that follow another one from the same side. */
    tail?: boolean;
    /** False where WhatsApp takes no reaction from us: the reactions shown are only read. */
    reactable?: boolean;
    /** A photo, file or card that can go to another chat: the forward button stays beside it. */
    forwardable?: boolean;
  }>(),
  { tail: true, reactable: true, forwardable: false },
);
const emit = defineEmits<{
  menu: [e: MouseEvent];
  react: [e: MouseEvent];
  select: [];
  media: [message: Message];
  retry: [key: string];
  discard: [key: string];
  /** Send a failed server message again. */
  resend: [messageId: string];
  /** Scroll to the quoted message. */
  jump: [messageId: string];
  forward: [];
}>();
/**
 * A removed message is a plain "deleted" bubble (who removed it, when, the time) until someone
 * allowed to see it opens it with the eye: then it is the original, failure and all.
 */
const revealed = ref(false);
const concealed = computed(() => !!props.message.removed && !revealed.value);
const reactions = computed(() => groupedReactions(props.message));
const reactionCount = computed(() =>
  reactions.value.reduce((count, reaction) => count + reaction.count, 0),
);
const visual = computed(
  () => props.message.kind === "image" || props.message.kind === "video",
);
/** Photos and videos without a caption carry the time over the picture, like WhatsApp. */
const richButtons = computed(() => shownButtons(props.message.rich?.buttons));
/**
 * A list message's button, and a template's "See all options", open what the contact sees after
 * tapping them: the rows, only to read (choosing is the contact's, on WhatsApp).
 */
const listOpen = ref(false);
const opensList = (type: string) =>
  (type === "list" && !!props.message.rich?.list) || type === "options";
const isChosen = (o: { id?: string; title: string }) => {
  const chosen = props.message.rich?.list?.chosen;
  if (!chosen) return false;
  return chosen.id && o.id ? chosen.id === o.id : chosen.title === o.title;
};
const listView = computed(
  () =>
    props.message.rich?.list ?? {
      title: t("templates.all_options"),
      options: (props.message.rich?.buttons ?? []).map((b) => ({
        title: b.text,
        description: null,
      })),
    },
);
const mediaOnly = computed(() => visual.value && !props.message.text);
/** Size read from the loaded picture when the server did not know it (received media). */
const natural = ref<{ width: number; height: number } | null>(null);
function measured(width: number, height: number) {
  if (width && height && !props.message.width)
    natural.value = { width, height };
}
const box = computed(() => {
  const width = props.message.width ?? natural.value?.width;
  const height = props.message.height ?? natural.value?.height;
  if (!width || !height) return undefined;
  const ratio = Math.min(Math.max(width / height, 0.75), 1.9);
  return { aspectRatio: String(ratio) };
});
const docMeta = computed(() => {
  const m = props.message;
  const parts: string[] = [];
  if (m.pages) parts.push(t("media.pages", { count: m.pages }));
  parts.push(extensionLabel(m.name ?? ""));
  if (m.size) parts.push(m.size);
  return parts.join(" · ");
});
/** Room kept at the end of the last line so the time fits beside the text, like WhatsApp. */
const metaLabel = computed(() =>
  [props.message.edited ? t("message.edited") : "", props.message.time]
    .filter(Boolean)
    .join(" "),
);
const metaIcons = computed(
  () =>
    (props.message.out && !props.message.deleted && !concealed.value ? 19 : 0) +
    (props.message.pinned && !concealed.value ? 14 : 0) +
    (props.message.starred ? 14 : 0),
);
const uploadPct = computed(() =>
  props.message.uploading === undefined
    ? null
    : Math.round(props.message.uploading * 100),
);
const ring = computed(() =>
  uploadPct.value === null
    ? undefined
    : { "--progress": uploadPct.value + "%" },
);
</script>
<template>
  <div
    class="message-line"
    :class="{ out: message.out, selected, selecting, continued: !tail }"
    @contextmenu.prevent="emit('menu', $event)"
    @click="selecting && emit('select')"
  >
    <input
      v-if="selecting"
      class="wa-check message-select"
      type="checkbox"
      :checked="selected"
      :aria-label="t('message.select', { text: message.text ?? '' })"
      @click.stop="emit('select')"
    />
    <div class="bubble-wrap">
      <article
        class="message-bubble"
        :class="[
          message.kind || 'text',
          {
            deleted: message.deleted,
            'media-only': mediaOnly,
            voice: message.voice,
            quoting: !!message.reply,
          },
        ]"
        :aria-label="message.text || message.name"
        :style="{ '--meta-icons': metaIcons + 'px' }"
      >
        <button
          v-if="!selecting && !message.outgoingKey"
          class="message-chevron"
          :aria-label="t('message.options')"
          @click.stop="emit('menu', $event)"
        >
          <Icon name="down" :size="18" :stroke="2.67" />
        </button>
        <p
          v-if="concealed"
          class="message-text message-removed-text"
          :data-meta="metaLabel"
        >
          <Icon name="ban" :size="16" />{{ message.removed }}
        </p>
        <template v-else>
          <div
            v-if="message.reply"
            class="quoted"
            :class="{ own: message.replyOut, linked: !!message.replyId }"
            :role="message.replyId ? 'button' : undefined"
            :tabindex="message.replyId ? 0 : undefined"
            @click.stop="message.replyId && emit('jump', message.replyId)"
            @keydown.enter.stop="
              message.replyId && emit('jump', message.replyId)
            "
          >
            <span class="quoted-body">
              <strong>{{ message.replyAuthor || t("message.contact") }}</strong>
              <span class="quoted-text">{{ message.reply }}</span>
            </span>
            <img
              v-if="message.replyThumb"
              class="quoted-thumb"
              :src="message.replyThumb"
              alt=""
            />
          </div>
          <template v-if="message.kind === 'image'"
            ><button
              class="image-message"
              :style="box"
              :aria-label="t('media.open')"
              @click="emit('media', message)"
            >
              <img
                :src="message.src"
                :alt="message.text || t('message.media.image')"
                loading="lazy"
                @load="
                  (e) =>
                    measured(
                      (e.target as HTMLImageElement).naturalWidth,
                      (e.target as HTMLImageElement).naturalHeight,
                    )
                "
              />
              <span v-if="ring" class="upload-ring" :style="ring"
                ><span>{{ uploadPct }}%</span></span
              >
            </button>
            <div
              v-if="message.text"
              class="message-text"
              :data-meta="metaLabel"
            >
              <WaText :text="message.text" blocks /></div></template
          ><template v-else-if="message.kind === 'video'"
            ><button
              class="image-message video-message"
              :style="box"
              :aria-label="t('media.open')"
              @click="emit('media', message)"
            >
              <video
                :src="message.src ? message.src + '#t=0.1' : undefined"
                preload="metadata"
                muted
                playsinline
                @loadedmetadata="
                  (e) =>
                    measured(
                      (e.target as HTMLVideoElement).videoWidth,
                      (e.target as HTMLVideoElement).videoHeight,
                    )
                "
              />
              <span v-if="!ring" class="video-play"
                ><Icon name="play" :size="30"
              /></span>
              <span v-if="message.duration" class="video-duration"
                ><Icon name="video" :size="14" />{{ message.duration }}</span
              >
              <span v-if="ring" class="upload-ring" :style="ring"
                ><span>{{ uploadPct }}%</span></span
              >
            </button>
            <div
              v-if="message.text"
              class="message-text"
              :data-meta="metaLabel"
            >
              <WaText :text="message.text" blocks /></div></template
          ><template v-else-if="message.kind === 'contact'"
            ><div
              v-for="(c, i) in message.contacts ?? [
                { name: message.name ?? '', phones: [] },
              ]"
              :key="i"
              class="contact-message"
            >
              <span class="contact-card-avatar"
                ><Icon name="user" :size="26"
              /></span>
              <div class="grow">
                <strong>{{ c.name }}</strong>
                <p v-for="phone in c.phones" :key="phone" class="small muted">
                  {{ phone }}
                </p>
              </div>
            </div></template
          ><template v-else-if="message.kind === 'sticker'"
            ><button class="sticker-message" @click="emit('media', message)">
              <img
                v-if="message.src"
                :src="message.src"
                :alt="t('message.media.sticker')"
              /><span v-else>{{ message.text }}</span>
            </button></template
          ><template v-else-if="message.kind === 'file'"
            ><a
              :href="
                message.uploading === undefined
                  ? message.downloadUrl || message.src
                  : undefined
              "
              :download="message.name"
              class="file-message"
              :aria-label="
                t('media.download_named', { name: message.name ?? '' })
              "
              ><span
                class="file-tile"
                :style="{ '--tone': fileTone(message.name ?? '') }"
                >{{ extensionLabel(message.name ?? "")
                }}<span
                  v-if="ring"
                  class="upload-ring small"
                  :style="ring" /></span
              ><span class="grow file-message-text"
                ><strong class="truncate">{{ message.name }}</strong
                ><small>{{ docMeta }}</small></span
              ><Icon
                v-if="!ring"
                class="file-download"
                name="download"
                :size="27"
                :stroke="2.44"
            /></a>
            <div
              v-if="message.text"
              class="message-text"
              :data-meta="metaLabel"
            >
              <WaText :text="message.text" blocks /></div></template
          ><template v-else-if="message.kind === 'audio'"
            ><AudioPlayer
              :src="message.src"
              :seed="message.id"
              :voice="message.voice"
              :duration-ms="message.durationMs"
              :name="message.name"
              :initials="message.initials"
              :out="message.out"
              :played="Boolean(message.playedAt)"
              :delivered="
                message.status === 'delivered' || message.status === 'read'
              "
            />
            <p v-if="message.transcript" class="audio-transcript">
              <span class="audio-transcript-label">{{
                t("message.transcript")
              }}</span>
              {{ message.transcript }}
            </p>
            <p
              v-else-if="message.transcriptStatus === 'pending'"
              class="audio-transcript muted"
            >
              {{ t("message.transcribing") }}
            </p></template
          ><template v-else-if="message.kind === 'poll'"
            ><div class="poll-message">
              <h3>{{ message.text }}</h3>
              <p class="small muted">
                <Icon name="checks" :size="16" />
                {{
                  message.multiple
                    ? t("message.poll.pick_many")
                    : t("message.poll.pick_one")
                }}
              </p>
              <button
                v-for="(opt, i) in message.options"
                :key="opt"
                :disabled="
                  !!message.expiresAt &&
                  Date.now() > new Date(message.expiresAt).getTime()
                "
                class="poll-option"
                @click="vote(message, i)"
              >
                <span
                  class="poll-radio"
                  :class="{ checked: message.selected?.includes(i) }"
                  ><Icon
                    v-if="message.selected?.includes(i)"
                    name="check"
                    :size="13" /></span
                ><span class="grow"
                  >{{ opt
                  }}<span class="poll-track"
                    ><i
                      :style="{
                        width:
                          ((message.votes?.[i] || 0) /
                            Math.max(1, ...(message.votes || [1]))) *
                            100 +
                          '%',
                      }" /></span></span
                ><span class="small">{{ message.votes?.[i] || 0 }}</span>
              </button>
              <p class="small muted" style="text-align: center">
                {{
                  t("message.poll.votes", {
                    count: message.votes?.reduce((a, b) => a + b, 0) || 0,
                  })
                }}
                <span v-if="message.anonymous"
                  >· {{ t("message.poll.anonymous") }}</span
                >
              </p>
            </div></template
          ><template v-else-if="message.kind === 'event'"
            ><div class="event-message">
              <Icon name="event" :size="32" />
              <div>
                <h3>{{ message.text }}</h3>
                <p class="small muted">{{ message.name }}</p>
                <button
                  class="wa-button soft compact"
                  @click="message.attending = !message.attending"
                >
                  {{
                    message.attending
                      ? t("message.event.going")
                      : t("message.event.join")
                  }}
                </button>
              </div>
            </div></template
          >
          <template v-else
            ><p v-if="message.rich?.header" class="tpl-header">
              {{ message.rich.header }}
            </p>
            <a
              v-if="message.linkPreview && !message.deleted"
              class="link-preview"
              :href="message.linkPreview.url"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
              ><img
                v-if="message.linkPreview.imageUrl"
                class="link-preview-image"
                :src="message.linkPreview.imageUrl"
                alt=""
                loading="lazy"
              /><span class="link-preview-text"
                ><strong>{{ message.linkPreview.title }}</strong
                ><span
                  v-if="message.linkPreview.description"
                  class="link-preview-description"
                  >{{ message.linkPreview.description }}</span
                ><span class="link-preview-site">{{
                  message.linkPreview.site
                }}</span></span
              ></a
            >
            <div class="message-text" :data-meta="metaLabel">
              <Icon v-if="message.deleted" name="ban" :size="16" /><WaText
                :text="message.text ?? ''"
                :blocks="!message.deleted"
              />
            </div>
            <p
              v-if="message.rich?.footer"
              class="tpl-footer"
              :data-meta="metaLabel"
            >
              {{ message.rich.footer }}
            </p></template
          >
          <small v-if="message.removed" class="message-removed-note">{{
            message.removed
          }}</small>
        </template>
        <span v-if="message.failed && !concealed" class="message-failed">{{
          message.failed
        }}</span>
        <span
          v-if="message.failed && message.outgoingKey && !concealed"
          class="message-retry"
        >
          <button type="button" @click="emit('retry', message.outgoingKey!)">
            {{ t("media.retry") }}</button
          ><button type="button" @click="emit('discard', message.outgoingKey!)">
            {{ t("media.discard") }}
          </button>
        </span>
        <span
          v-else-if="
            message.failed && message.out && message.canRetry && !concealed
          "
          class="message-retry"
        >
          <button type="button" @click="emit('resend', message.id)">
            {{ t("media.retry") }}
          </button>
        </span>
        <div class="message-meta" :class="{ 'message-queued': message.queued }">
          <Icon
            v-if="message.pinned && !concealed"
            name="pin"
            :size="11"
          /><Icon v-if="message.starred" name="star" :size="11" /><span
            v-if="message.edited"
            >{{ t("message.edited") }}</span
          ><time>{{ message.time }}</time
          ><MessageStatus
            v-if="message.out && !message.deleted && !concealed"
            :status="messageDelivery(message)"
          />
        </div>
        <!-- The buttons the contact sees under the message (they only answer on WhatsApp). -->
        <div v-if="richButtons.length && !concealed" class="tpl-buttons">
          <template v-for="(b, i) in richButtons" :key="i"
            ><button
              v-if="opensList(b.type)"
              type="button"
              class="tpl-button"
              @click.stop="listOpen = true"
            >
              <Icon :name="buttonIcon(b.type)" :size="16" />{{ b.text }}
            </button>
            <span v-else class="tpl-button"
              ><Icon :name="buttonIcon(b.type)" :size="16" />{{ b.text }}</span
            ></template
          >
        </div>
        <Modal
          v-if="listOpen"
          :open="listOpen"
          :title="listView.title"
          @close="listOpen = false"
        >
          <ul class="wa-list-sheet">
            <li
              v-for="(o, i) in listView.options"
              :key="i"
              :class="{ chosen: isChosen(o) }"
            >
              <span class="grow"
                ><strong>{{ o.title }}</strong
                ><small v-if="o.description">{{ o.description }}</small
                ><small v-if="isChosen(o)" class="wa-list-chosen">{{
                  t("message.list_chosen")
                }}</small></span
              ><span class="wa-list-radio" aria-hidden="true" />
            </li>
          </ul>
          <p class="small muted">{{ t("message.list_view_only") }}</p>
        </Modal>
      </article>
      <component
        :is="reactable ? 'button' : 'span'"
        v-if="reactions.length && !concealed"
        class="message-reactions"
        :class="{ own: reactions.some((reaction) => reaction.own) }"
        :aria-label="reactable ? t('message.see_reactions') : undefined"
        @click="reactable && emit('react', $event)"
      >
        <span v-for="reaction in reactions" :key="reaction.emoji">{{
          reaction.emoji
        }}</span
        ><span v-if="reactionCount > 1" class="reaction-count">{{
          reactionCount
        }}</span></component
      ><button
        v-if="message.removed && !selecting"
        type="button"
        class="side-trigger"
        :aria-label="
          revealed ? t('message.removed_hide') : t('message.removed_show')
        "
        :aria-pressed="revealed"
        @click="revealed = !revealed"
      >
        <Icon :name="revealed ? 'eyeOff' : 'eye'" :size="18" /></button
      ><button
        v-else-if="forwardable && !selecting && !message.outgoingKey"
        type="button"
        class="side-trigger"
        :aria-label="t('message.forward')"
        @click="emit('forward')"
      >
        <Icon name="forward" :size="18" /></button
      ><button
        v-if="
          reactable &&
          !selecting &&
          !message.deleted &&
          !message.outgoingKey &&
          !message.removed
        "
        class="reaction-trigger"
        :aria-label="t('message.react')"
        @click="emit('react', $event)"
      >
        <Icon name="smile" :size="18" />
      </button>
    </div>
  </div>
</template>
